import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./auth.css";
import video from "../../assets/hero-video.mp4";
import logo from "../../assets/ZAUNTRIP-Logo.png"; // Import your logo

const API_BASE = "https://crmapi.zauntrip.com/api/auth";

const SignIn = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: ""
  });
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [showOTPInput, setShowOTPInput] = useState(false);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleOTPChange = (e) => {
    setOtp(e.target.value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      if (data.requiresVerification) {
        setRequiresOTP(true);
        setShowOTPInput(true);
        alert("OTP has been sent to your email. Please check your inbox.");
      } else if (data.token) {
        localStorage.setItem("authToken", data.token);
        
        localStorage.setItem('authUser', JSON.stringify({
          email: data.user.email,
          full_name: data.user.full_name,
          isAdmin: data.user.isAdmin,
          id: data.user.id
        }));
        
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email,
          otp: otp
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "OTP verification failed");
      }

      if (data.token) {
        localStorage.setItem("authToken", data.token);
        
        localStorage.setItem('authUser', JSON.stringify({
          email: data.user.email,
          full_name: data.user.full_name,
          isAdmin: data.user.isAdmin,
          id: data.user.id
        }));
        
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.message || "Invalid OTP. Please try again.");
      console.error("OTP verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to resend OTP");
      }

      if (data.requiresVerification) {
        alert("New OTP has been sent to your email.");
      }
    } catch (err) {
      setError(err.message || "Failed to resend OTP");
      console.error("Resend OTP error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Video Section */}
      <div className="video-section">
        <video 
          className="video-background" 
          autoPlay 
          muted 
          loop 
          playsInline
        >
          <source 
            src={video}
            type="video/mp4" 
          />
          Your browser does not support the video tag.
        </video>
        <div className="video-overlay"></div>
        <div className="logo-container">
          {/* Your actual logo */}
          <img 
            src={logo} 
            alt="ZaunTrip Logo" 
            style={{ 
              width: '200px', 
              height: 'auto',
              marginBottom: '20px'
            }} 
          />
          <h1>Welcome Back</h1>
          <p>Sign in to access your CRM dashboard and manage your business efficiently.</p>
        </div>
      </div>

      {/* Right Form Section */}
      <div className="form-section">
        <form className="auth-card" onSubmit={showOTPInput ? handleOTPVerification : handleLogin}>
          <h2>Sign In</h2>
          
          {error && <div className="error-message">{error}</div>}

          {!showOTPInput ? (
            <>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  required
                />
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </>
          ) : (
            <>
              <div className="otp-header">
                <h3>OTP Verification Required</h3>
                <p>Please enter the 6-digit OTP sent to {form.email}</p>
              </div>

              <div className="form-group">
                <label htmlFor="otp">One-Time Password (OTP) *</label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={handleOTPChange}
                  required
                  maxLength="6"
                  pattern="[0-9]{6}"
                  inputMode="numeric"
                />
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

              <button 
                type="button" 
                className="resend-btn"
                onClick={handleResendOTP}
                disabled={loading}
              >
                Resend OTP
              </button>

              <button 
                type="button" 
                className="back-btn"
                onClick={() => {
                  setShowOTPInput(false);
                  setError("");
                  setOtp("");
                }}
              >
                Back to Login
              </button>
            </>
          )}

          <div className="auth-footer">
            <p className="auth-link" onClick={() => navigate("/create-user")}>
              Don't have an account? <span>Create User</span>
            </p>
            
            {!showOTPInput && (
              <p className="auth-link" onClick={() => navigate("/forgot-password")}>
                Forgot Password? <span>Reset</span>
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;